import { z } from 'zod';
import { AuditSchema, IdSchema } from './common.ts';
import { multiplyMoney, type Money } from '../money.ts';

/**
 * A tax rate, as a **company record** (P5-12a).
 *
 * `TASKS.md` is explicit about this — "taxes are a rate record per company,
 * not a number typed per invoice" — and the reason is the one that applies
 * to every rate in an accounting system: a rate has a name people say out
 * loud ("Arizona TPT"), it maps to something in the accounting software,
 * and two invoices issued the same week must not be able to disagree about
 * what 8.6% means because somebody typed it twice.
 *
 * **Millionths**, like `markupMillionths`, so 8.6% is
 * `86_000`. Written in the same units deliberately: a second money-ish
 * scale in one product is how a figure ends up a hundred times wrong, and
 * that has already happened here once (see `CoLine.markupMillionths` and
 * P5-00). `taxRateFromPercent` is the only way it should be constructed.
 */
export const TAX_UNITS_PER_PERCENT = 10_000;

export const TaxRateSchema = AuditSchema.extend({
  id: IdSchema,
  companyId: IdSchema,
  /** What it is called on an invoice: "Arizona TPT". */
  name: z.string().trim().min(1).max(80),
  /** Millionths: 8.6% is 86_000. Capped at 100%. */
  rateMillionths: z.number().int().min(0).max(1_000_000),
  /**
   * Whether it is offered on a new invoice.
   *
   * Retired rather than deleted, for the reason a cost code is: an invoice
   * issued two years ago points at the rate it used, and deleting the rate
   * would turn that invoice's tax into a figure with no explanation.
   */
  active: z.boolean().default(true),
  /** The id this maps to in the accounting software. Phase 7 uses it. */
  externalId: z.string().trim().max(120).optional(),
});
export type TaxRate = z.infer<typeof TaxRateSchema>;

export function taxRateFromPercent(percent: number): number {
  return Math.round(percent * TAX_UNITS_PER_PERCENT);
}

export function taxRatePercent(rateMillionths: number): number {
  return rateMillionths / TAX_UNITS_PER_PERCENT;
}

/** `8.6%`, for a picker and for an invoice's own line. */
export function formatTaxRate(rate: Pick<TaxRate, 'name' | 'rateMillionths'>): string {
  return `${rate.name} (${taxRatePercent(rate.rateMillionths).toFixed(2)}%)`;
}

/**
 * The tax on an amount at a rate.
 *
 * Rounded once, by `multiplyMoney`, half away from zero — the same rule as
 * a line's markup, for the same reason: a person checking the arithmetic by
 * hand rounds that way, and two different rounding rules inside one invoice
 * is a penny somebody has to explain.
 */
export function taxOn(amount: Money, rateMillionths: number): Money {
  return multiplyMoney(amount, rateMillionths / 1_000_000);
}
